import { Alert, Box, Button, Chip, Divider, Typography } from '@mui/material';
import { StylesForm } from '../../../styles/global';
import {
  formatCourseLong,
  formatDepartmentLong,
} from '../../../utils/formatAttributes';
import { useAuth } from '../../../utils/AuthContext';
import { useEffect, useState } from 'react';
import {
  ClientWizard,
  ExpectedNumberOfMembersWizard,
  ProjectIdentityWizard,
} from './Wizards';
import { ArrowRightAlt } from '@mui/icons-material';
import { patchProject } from '../../../utils/API';
import { useFeedback } from '../../../utils/FeedbackContext';

const SlateBaseText = ({ children }) => {
  return (
    <>
      <Typography {...StylesForm.text('p')} color="darkslategray">
        {children}
      </Typography>
    </>
  );
};

// Widget for Project Details
const InformProjectDetails = ({ project }) => {
  const { user } = useAuth();
  const IsAdmin = user?.role === 'ADMIN' || user?.role === 'FACULTY';
  const IsStudent = user?.role === 'STUDENT';
  const [cta, setCta] = useState('edit');
  const [form, setForm] = useState(undefined);
  const { notifyError, notifySuccess } = useFeedback();

  const handleSaveChanges = () => {
    patchProject(project.data._id, form)
      .then((res) => {
        notifySuccess('Project updated successfully!');
        setCta('edit');

        setTimeout(() => {
          window.location.reload();
        }, 4000);
      })
      .catch((err) => {
        notifyError(
          err?.response?.data?.message || 'Failed to update project.'
        );
      });
  };

  useEffect(() => {
    if (form === undefined && !!project) {
      setForm({
        ...project.data,
      });
    }
  }, [project, form]);

  const disabled =
    form?.title === project?.data.title &&
    form?.description === project?.data.description &&
    form?.clients === project?.data.clients &&
    form?.expectedNumberOfMembers === project?.data.expectedNumberOfMembers;

  const viewProject = !!project && (
    <>
      <Typography {...StylesForm.text('p')}>
        Project Title:{' '}
        <SlateBaseText>
          <strong>{`${project.data.title}`}</strong>
        </SlateBaseText>
      </Typography>

      <Typography {...StylesForm.text('p')}>
        Course Equivalent:{' '}
        <SlateBaseText>{`${formatCourseLong(
          project.data.course
        )}`}</SlateBaseText>
      </Typography>

      <Typography {...StylesForm.text('p')}>
        Department:{' '}
        <SlateBaseText>{`${formatDepartmentLong(
          project.data.department
        )}`}</SlateBaseText>
      </Typography>

      <Typography {...StylesForm.text('p')}>
        Competition Participant Year:{' '}
        <SlateBaseText>{`${project.data.year}`}</SlateBaseText>
      </Typography>

      <Typography {...StylesForm.text('p')}>
        Client(s): <SlateBaseText>{`${project.data.clients}`}</SlateBaseText>
      </Typography>

      <Typography {...StylesForm.text('p')}>
        Instructor(s):{' '}
        <SlateBaseText>{`${project.data.instructor}`}</SlateBaseText>
      </Typography>
    </>
  );

  const changesView = !!form && !!project && (
    <Box
      sx={{
        border: '1px dashed lightgray',
        padding: 2,
        borderRadius: 2,
        marginY: 1,
      }}
    >
      <SlateBaseText>Current Changes* </SlateBaseText>
      {form.title !== project.data.title && (
        <>
          <Typography {...StylesForm.text('body2')} color="gray">
            Project Title:
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              marginY: 1,
              flexWrap: 'wrap',
              padding: 1,
              border: '1px solid lightgrey',
              borderRadius: 8,
              alignContent: 'center',
            }}
          >
            <Chip label={`From: ${project.data.title}`} />
            <ArrowRightAlt />
            <Chip label={`To: ${form.title}`} />
          </Box>
        </>
      )}
      {form.clients !== project.data.clients && (
        <>
          <Typography {...StylesForm.text('body2')} color="gray">
            Project Clients:
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              marginY: 1,
              flexWrap: 'wrap',
              padding: 1,
              border: '1px solid lightgrey',
              borderRadius: 8,
              alignContent: 'center',
            }}
          >
            <Chip label={`From: ${project.data.clients}`} />
            <ArrowRightAlt />
            <Chip label={`To: ${form.clients}`} />
          </Box>
        </>
      )}
      {form.expectedNumberOfMembers !==
        project.data.expectedNumberOfMembers && (
        <>
          <Typography {...StylesForm.text('body2')} color="gray">
            Expected # of Members:
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              marginY: 1,
              flexWrap: 'wrap',
              padding: 1,
              border: '1px solid lightgrey',
              borderRadius: 8,
              alignContent: 'center',
            }}
          >
            <Chip label={`From: ${project.data.expectedNumberOfMembers}`} />
            <ArrowRightAlt />
            <Chip label={`To: ${form.expectedNumberOfMembers}`} />
          </Box>
        </>
      )}
    </Box>
  );

  const editProject = !!project && (
    <>
      <Divider sx={{ marginY: 1 }} />
      <Box
        sx={{
          border: '1px dashed lightgray',
          padding: 2,
          borderRadius: 2,
          gap: 2,
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <ProjectIdentityWizard
          title={`Edit Project Identity`}
          variant={'edit'}
          buttonLabel={'Edit Project Title'}
          onChange={(title, description) =>
            setForm({ ...form, title, description })
          }
        />
        <ClientWizard
          title={`Edit Client(s)`}
          variant={'edit'}
          buttonLabel={'Edit Client(s)'}
          onChange={(clients) => setForm({ ...form, clients })}
        />
        <ExpectedNumberOfMembersWizard
          title={`Project Team Members`}
          variant={'edit'}
          buttonLabel={'Project Team Members'}
          onChange={(expectedNumberOfMembers) =>
            setForm({ ...form, expectedNumberOfMembers })
          }
        />
      </Box>
      {changesView}
      {IsStudent && (
        <Alert variant="outlined" severity="info" fontFamily={'sans-serif'}>
          You can only edit the project title, clients, and expected number of
          members. For other changes, please contact an administrator. Don't
          forget to save your changes above!
        </Alert>
      )}
    </>
  );

  return (
    <>
      {project && (
        <>
          {/* Title: Project Details */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography {...StylesForm.text('h6')}>Project Details</Typography>
            {(IsAdmin || IsStudent) && (
              <>
                {cta === 'edit' && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setCta('save')}
                  >
                    Edit Project
                  </Button>
                )}
                {cta === 'save' && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSaveChanges}
                    disabled={disabled}
                  >
                    Save Changes
                  </Button>
                )}
              </>
            )}
          </Box>
          <Divider />

          {/* Start of Info: Title, Course, Department, Year, Clients, Instructor */}

          {cta === 'edit' && viewProject}
          {cta === 'save' && editProject}

          {/* End of Info */}

          <Divider />
        </>
      )}
    </>
  );
};

export default InformProjectDetails;
